import React from 'react';

export interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'pending' | 'alert' | 'info' | 'warning';
  dot?: boolean;
  pulse?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Badge Component v2 - FacturXPro Design System
 *
 * Badge avec variantes couleurs, dot optionnel et animation pulse
 *
 * @example
 * ```tsx
 * // Badge success avec dot
 * <Badge variant="success" dot>
 *   Payé
 * </Badge>
 *
 * // Badge alert avec pulse
 * <Badge variant="alert" dot pulse>
 *   En retard
 * </Badge>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Badges & Pills"
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'info',
  dot = false,
  pulse = false,
  size = 'md',
  className = '',
}) => {
  const variantClasses = {
    success: 'bg-teal-50 text-teal-700 border-teal-200',
    pending: 'bg-gray-100 text-gray-700 border-gray-200',
    alert: 'bg-red-50 text-red-700 border-red-200',
    warning: 'bg-orange-50 text-orange-700 border-orange-200',
    info: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  const dotColors = {
    success: 'bg-teal-500',
    pending: 'bg-gray-400',
    alert: 'bg-red-500',
    warning: 'bg-orange-500',
    info: 'bg-blue-500',
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5',
  };

  return (
    <span
      className={`
        inline-flex
        items-center
        gap-1.5
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        border
        rounded-full
        font-medium
        ${className}
      `.trim()}
    >
      {dot && (
        <span className="relative flex h-2 w-2">
          {pulse && (
            <span
              className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${dotColors[variant]}`}
            />
          )}
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${dotColors[variant]}`}
          />
        </span>
      )}
      {children}
    </span>
  );
};

export default Badge;
